<template>
  <div v-loading="loading" element-loading-text="しばらくお待ちください。">
    <div class="mb-x2" layout layout-align="space-between-start">
      <el-button type="default" @click="back"><i class="mdi mdi-keyboard-backspace"></i>戻る</el-button>
    </div>
    <div class="used-code-import" v-if="is_import">
      <div class="message message-info mb-x3">
        <div>使用済みシリアルコードを入力してください。</div>
        <div>複数入力する場合、1シリアルコード１行となるよう改行してください。</div>
      </div>
      <div class="input-container mb-x3">
        <el-input
          class="body-textarea"
          v-model="body"
          type="textarea"
          :class="{ error: $v.body.required && $v.body.$error, success: $v.body.required && !$v.body.invalid }"
          placeholder="使用済みシリアルコードを入力"
          @blur="$v.body.$touch"
        ></el-input>
        <div class="input-invalid-text" v-if="maxSerialCodeError">
          一度にインポートできる件数（10,000件）を超えています。
        </div>
      </div>
      <div class="footer">
        <el-button type="primary" :disabled="disabled" @click="importUsedCodes"
          ><i class="mdi mdi-upload"></i>インポート実行</el-button
        >
      </div>
    </div>
    <div class="used-code-reset" v-if="is_used_code_reset">
      <div class="message message-error mb-x3">
        <div>使用済みのシリアルコードの割り当てを一括解除してよろしいですか?</div>
        <div>この操作は取り消しできません。</div>
      </div>
      <div class="footer">
        <el-button type="primary" @click="resetUsedCode"><i class="mdi mdi-playlist-play"></i>一括解除を実行</el-button>
      </div>
    </div>
    <div class="serial-code-reset" v-if="is_code_reset">
      <div class="message message-error mb-x3">
        <div>未割り当てのシリアルコードを全て削除してよろしいですか?</div>
        <div>この操作は取り消しできません。</div>
      </div>
      <div class="footer">
        <el-button type="primary" @click="resetCode"><i class="mdi mdi-playlist-play"></i>一括削除を実行</el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.footer {
  text-align: right;
}

.body-textarea {
  textarea {
    height: 260px;
  }
}
</style>
<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import api from '@/client/core/api'
import { TrackingService } from '@/client/services'
import { useTwitterSerialCodeDialogStore } from '@/client/store/useTwitterSerialCodeDialogStore'

export default defineComponent({
  props: {
    screen: String,
    isChangeWithoutSubmit: Boolean
  },
  data() {
    return {
      body: '',
      loading: false
    }
  },
  watch: {
    screen() {
      this.reset()
    },
    isFormChanged(value: boolean) {
      this.$emit('update:isChangeWithoutSubmit', value)
    }
  },
  computed: {
    ...mapState(useTwitterSerialCodeDialogStore, ['prizeId', 'campaignId']),
    is_import() {
      return this.screen === 'import'
    },
    is_used_code_reset() {
      return this.screen === 'used_code_reset'
    },
    is_code_reset() {
      return this.screen === 'code_reset'
    },
    isFormChanged() {
      return this.body !== ''
    },
    maxSerialCodeError() {
      if (!this.body.trim()) {
        return false
      }

      return this.body.trim().split('\n').length > 10000
    },
    disabled() {
      return !this.body || !this.body.trim() || this.maxSerialCodeError || this.loading
    }
  },
  methods: {
    ...mapActions(useTwitterSerialCodeDialogStore, ['fetchSerialCodeList']),

    /**
     * 一覧に戻る
     */
    back() {
      TrackingService.sendEvent(
        `click:当選メッセージの管理|${this.screen === 'used_code_reset' ? '使用済み' : '未割り当て'}シリアルコード|戻る`
      )

      if (!this.isChangeWithoutSubmit) {
        return this.$emit('back')
      }

      const confirm = window.confirm('編集内容が保存されていません。ダイアログを閉じてよろしいですか?')

      if (!confirm) {
        return
      }

      this.$emit('update:isChangeWithoutSubmit', false)
      this.$emit('back')
    },

    /**
     * 使用済みコードのインポート
     */
    async importUsedCodes() {
      const serialCodes = this.body.split('\n').filter(code => code)

      let confirmMessage = `${serialCodes.length}件の使用済みコードをインポートします。`

      confirmMessage += `\n${'処理を実行してよろしいですか?'}`
      confirmMessage += `\n${'この操作は取り消しできません。'}`
      const confirm = window.confirm(confirmMessage)

      if (!confirm) {
        return
      }

      this.loading = true

      const result = await api.post('/twitter_serial_codes/used_imports', {
        prizeId: this.prizeId,
        serialCodes
      })

      if (result && result.data !== undefined) {
        const title = `${result.data}件の使用済みコードをインポートしました。`

        this.$notify({
          title,
          message: '',
          customClass: 'success',
          duration: 5000
        })

        this.loading = false

        this.$emit('update:isChangeWithoutSubmit', false)
        this.fetchSerialCodeList()
        this.$nextTick(() => {
          this.back()
        })
        return
      }

      const title = '使用済みコードのインポートに失敗しました。'
      const message = '恐れ入りますが、時間を置いて再度お試しください。'

      this.$notify({
        title,
        message,
        customClass: 'danger',
        duration: 5000
      })

      this.loading = false
    },

    /**
     * 使用済みコード解除
     */
    async resetUsedCode() {
      TrackingService.sendEvent('click:当選メッセージの管理|使用済みシリアルコード|一括解除を実行')

      this.loading = true

      const result = await api.post('/twitter_serial_codes/used_resets', {
        prizeId: this.prizeId
      })

      if (result && result.data) {
        const title = '使用済みシリアルコードの割り当てを一括解除しました。'

        this.$notify({
          title,
          message: '',
          customClass: 'success',
          duration: 5000
        })

        this.loading = false
        this.fetchSerialCodeList()
        this.back()
        return
      }

      const title = '使用済みシリアルコードの割り当て解除に失敗しました。'
      const message = '恐れ入りますが、時間をおいて再度お試しください。'

      this.$notify({
        title,
        message,
        customClass: 'danger',
        duration: 5000
      })

      this.loading = false
    },

    /**
     * 未割り当てコード削除
     */
    async resetCode() {
      TrackingService.sendEvent('click:当選メッセージの管理|未割り当てシリアルコード|一括解除を実行')

      this.loading = true

      const result = await api.post('/twitter_serial_codes/resets', {
        prizeId: this.prizeId
      })

      if (result && result.data) {
        const title = 'シリアルコードを削除しました。'

        this.$notify({
          title,
          message: '',
          customClass: 'success',
          duration: 5000
        })

        this.loading = false
        this.fetchSerialCodeList()
        this.back()
        return
      }

      const title = 'シリアルコードの削除に失敗しました。'
      const message = '恐れ入りますが、時間をおいて再度お試しください。'

      this.$notify({
        title,
        message,
        customClass: 'danger',
        duration: 5000
      })

      this.loading = false
    },

    /**
     * リセット
     */
    reset() {
      this.body = ''
    }
  },
  validations: {
    body: {
      required
    }
  }
})
</script>
